import "./style.scss";

import { Button, Pagination as AntdPagination, Select } from "antd";
import {
  LiaLongArrowAltLeftSolid,
  LiaLongArrowAltRightSolid,
} from "react-icons/lia";

import { DEFAULT_PAGE_SIZE_OPTIONS } from "@/utils/constant";

interface PaginationProps {
  isLoading: boolean;
  current?: number;
  pageSize?: number;
  total?: number;
  nextLink?: string;
  totalRecordCountLimitExceeded?: boolean;
  handleChange?: (page: number, pageSize: number) => void;
}
/**
 * `Pagination` is a component that provides a user interface for navigating
 * through paged data. It displays page numbers and next/previous buttons,
 * allowing the user to select a specific page or move sequentially through the pages.
 *
 * Props:
 * - `current`: The current active page.
 * - `total`: The total number of items in the data source.
 * - `pageSize`: The number of items per page.
 * - `onChange`: Function to call when the page number changes.
 * - `nextLink`: The dataverse API has the limit of 5k items per API call. So inorder to fetch the next set of items, we need to use the `nextLink` property.'
 * - `totalRecordCountLimitExceeded`: A boolean value to indicate if the total number of items in the data source exceeds the limit of 5k items per API call.
 * Example usage:
 * This example shows page numbers and next/previous buttons.
 * ```jsx
 * <Pagination current={1} total={100} pageSize={10} onChange={handlePageChange} />
 * ```
 * Example with next link:
 * While using nextlink we don't need to pass the total number of items in the data source. So the pagination
 * only contain next and previous buttons with current page index
 * ```jsx
 * <Pagination current={1} total={100} pageSize={10} nextLink={nextLink} totalRecordCountLimitExceeded={true} onChange={handlePageChange} />
 * ```
 */
export const Pagination = ({
  isLoading,
  current,
  pageSize,
  total = 0,
  handleChange,
  nextLink,
  totalRecordCountLimitExceeded,
}: PaginationProps) => {
  const handlePrev = () => {
    if (current && pageSize) {
      handleChange?.(current - 1, pageSize);
    }
  };

  const handleNext = () => {
    if (current && pageSize) {
      handleChange?.(current + 1, pageSize);
    }
  };


  if (isLoading) {
    return null;
  }

  return (
    <div className="pagination_wrapper">
      {totalRecordCountLimitExceeded ? (
        <div className="flex items-center gap-x-4">
          <span>{total >= 5000 ? " 5000+" : total} items</span>
          <div className="flex items-center gap-x-2">
            <Button
              type="link"
              icon={<LiaLongArrowAltLeftSolid />}
              disabled={current === 1}
              onClick={handlePrev}
              className="prev_btn inline-flex items-center justify-center p-0 text-primary disabled:text-neutral-400"
            />
            <span>Page {current}</span>
            <Button
              type="link"
              icon={<LiaLongArrowAltRightSolid />}
              disabled={!nextLink}
              onClick={handleNext}
              className="next_btn inline-flex items-center  justify-center p-0 text-primary-500 disabled:text-neutral-400"
            />
          </div>
          {/* <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            options={DEFAULT_PAGE_SIZE_OPTIONS}
          /> */}
        </div>
      ) : (
        <AntdPagination
          current={current}
          pageSize={pageSize}
          total={total}
            onChange={handleChange}
            showSizeChanger={false}
          showTotal={(total) => `Total ${total} items`}
        />
      )}
    </div>
  );
};
